import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Options, SortableEvent } from 'sortablejs';
import Swal from 'sweetalert2';
import { IWidget, IWidgetItem } from '../../../core/models/widget.model';
import { AppService } from '../../../store/app.service';
import { AddWidgetItemComponent } from '../add-widget-item/add-widget-item.component';
import { DashboardApiService } from '../dashboard.apiservice';
@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss']
})

export class WidgetComponent implements OnInit, OnChanges, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() widget!: IWidget;
  editVisible: boolean = false;
  newName: any = '';
  changeLayoutStarted: boolean = false;
  removeItemsList: string[] = [];
  localItems: IWidgetItem[] = [];
  originalItems: IWidgetItem[] = [];
  isSaving: boolean = false;

  constructor(private appService: AppService,
    private dashboardApiService: DashboardApiService,
    private modalService: NgbModal,
    private router: Router,
    private translate: TranslateService) {
    this.appService.getChangeLayoutValue()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: boolean) => { this.changeLayoutStarted = response; });
  }

  ngOnInit(): void {
    this.syncLocalItems();

    if (this.widget.items.length && this.widget.items[0].value === '') {
      this.appService.fetchWidgetValues(this.widget.id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widget && !this.editVisible) {
      this.syncLocalItems();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  refresh() {
    this.widget.items.forEach(item => item.value = '');
    this.appService.fetchWidgetValues(this.widget.id);
  }

  removeWidget() {
    Swal.fire({
      title: this.translate.instant('ARE_YOU_SURE'),
      text: this.translate.instant('CANNOT_REVERT_ACTION'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('YES_DELETE_IT'),
      cancelButtonText: this.translate.instant('CANCEL')
    }).then((result) => {
      if (result.isConfirmed) {
        this.appService.removeWidget$(this.widget.id);
      }
    });
  }

  addItem() {
    const dialogRef = this.modalService.open(AddWidgetItemComponent);
    dialogRef.componentInstance.widgetId = this.widget.id;
    dialogRef.componentInstance.widgetName = this.widget.name;
  }

  edit() {
    this.editVisible = true;
    this.newName = this.widget.name;
    this.removeItemsList = [];
    this.localItems = this.cloneItems(this.widget.items);
    this.originalItems = this.cloneItems(this.widget.items);
  }

  addItemToRemoveList(itemId: string) {
    if (this.isItemToBeDeleted(itemId)) {
      this.removeItemsList = this.removeItemsList.filter(id => id !== itemId);
      return;
    }

    this.removeItemsList.push(itemId);
  }

  isItemToBeDeleted(itemId: string) {
    return this.removeItemsList.indexOf(itemId) > -1;
  }

  async completeEdit() {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;

    try {
      const trimmedName = (this.newName || '').trim();
      if (trimmedName && trimmedName !== this.widget.name) {
        await this.dashboardApiService.renameWidget(this.widget.id, trimmedName).toPromise();
      }

      if (this.removeItemsList.length) {
        await this.dashboardApiService.editWidgetItems({
          WidgetId: this.widget.id,
          RemoveItems: this.removeItemsList
        }).toPromise();
      }

      const orderUpdates = this.getItemOrderUpdates();
      for (const orderUpdate of orderUpdates) {
        await this.dashboardApiService.reorderWidgetItem(orderUpdate.itemId, orderUpdate.newOrder).toPromise();
      }

      this.resetEditState();
      this.appService.fetchWidgets();
    } finally {
      this.isSaving = false;
    }
  }

  cancelEdit() {
    this.syncLocalItems();
    this.resetEditState();
  }

  itemClicked(item: any) {
    if (this.editVisible) {
      return;
    }

    const query = { queryParams: { savedsearchid: item.savedSearchId } };
    if (item.resultType.toLowerCase() === 'user')
      this.router.navigate(['/pages/users'], query);

    else if (item.resultType.toLowerCase() === 'campaign')
      this.router.navigate(['/pages/campaigns'], query);

    else if (item.resultType.toLowerCase() === 'company')
      this.router.navigate(['/pages/companies'], query);

    else if (item.resultType.toLowerCase() === 'client')
      this.router.navigate(['/pages/clients'], query);

    else if (item.resultType.toLowerCase() === 'prospect')
      this.router.navigate(['/pages/prospects'], query);

    else if (item.resultType.toLowerCase() === 'tag')
      this.router.navigate(['/pages/tags'], query);

    else if (item.resultType.toLowerCase() === 'lead')
      this.router.navigate(['/pages/leads'], query);

    else if (item.resultType.toLowerCase() === 'task')
      this.router.navigate(['/pages/tasks'], query);
  }

  onWidgetItemDrop(event: SortableEvent) {
    if (!this.editVisible) {
      return;
    }
  }

  getSortableOptions(widget: IWidget) {
    const sortableOptions: Options = {
      group: 'widgetItems' + widget.id,
      handle: '.itemdraghandler',
      disabled: !this.editVisible,
      sort: this.editVisible,
      onUpdate: (event) => this.onWidgetItemDrop(event),
      dataIdAttr: 'id'
    };
    return sortableOptions;

  }

  private syncLocalItems() {
    const items = this.widget?.items ?? [];
    this.localItems = this.cloneItems(items);
    this.originalItems = this.cloneItems(items);
    this.newName = this.widget?.name ?? '';
  }

  private cloneItems(items: IWidgetItem[]): IWidgetItem[] {
    return items.map(item => ({ ...item }));
  }

  private getItemOrderUpdates() {
    return this.localItems
      .filter(item => !this.isItemToBeDeleted(item.id))
      .map((item, index) => ({
        itemId: item.id,
        newOrder: index + 1,
        originalOrder: this.originalItems.find(originalItem => originalItem.id === item.id)?.order
      }))
      .filter(item => item.originalOrder !== item.newOrder)
      .map(item => ({ itemId: item.itemId, newOrder: item.newOrder }));
  }

  private resetEditState() {
    this.editVisible = false;
    this.removeItemsList = [];
  }
}